/**
 * Client-Side Snap-to-Path Algorithm
 *
 * Projects a GPS point onto the nearest route polyline.
 * Runs entirely in the browser — no API calls needed.
 *
 * Algorithm:
 * 1. For each segment of the polyline, find closest point on that segment
 * 2. Pick the segment with smallest perpendicular distance
 * 3. Calculate cumulative "along meters" to that point
 *
 * Uses simple equirectangular projection (accurate enough at Kärnten latitudes).
 */

// ── Types ───────────────────────────────────────────────────────

export interface GpsPoint {
  latitude: number
  longitude: number
}

export interface SnapResult {
  /** Snapped latitude */
  snappedLat: number
  /** Snapped longitude */
  snappedLon: number
  /** Distance from raw point to snapped point (meters) */
  distanceMeters: number
  /** Distance along the route from start (meters) */
  alongMeters: number
  /** Lateral offset (same as distanceMeters for our purposes) */
  lateralOffsetMeters: number
  /** Route ID this was snapped to */
  routeId: number
}

export interface RoutePolyline {
  routeId: number
  points: GpsPoint[]
  /** Pre-computed cumulative distances (meters) — same length as points */
  cumulativeMeters: number[]
  /** Total route length in meters */
  totalMeters: number
}

// ── Constants ───────────────────────────────────────────────────

/** Meters per degree latitude (approximately constant) */
const METERS_PER_DEG_LAT = 111_320

// metersPerDegLng not needed — we use cosLat * METERS_PER_DEG_LAT inline

// ── Route Pre-processing ────────────────────────────────────────

/**
 * Pre-process a route's GPS points for efficient snapping.
 * Calculates cumulative distances along the route.
 * Call this once when loading route data.
 */
export function buildRoutePolyline(routeId: number, points: GpsPoint[]): RoutePolyline {
  const cumulativeMeters: number[] = [0]
  let total = 0

  for (let i = 1; i < points.length; i++) {
    const d = fastDistance(points[i - 1], points[i])
    total += d
    cumulativeMeters.push(total)
  }

  return {
    routeId,
    points,
    cumulativeMeters,
    totalMeters: total,
  }
}

// ── Snap Algorithm ──────────────────────────────────────────────

/**
 * Snap a GPS point to the nearest point on a route polyline.
 * Runs in O(n) where n = number of route segments.
 */
export function snapToRoute(
  lat: number,
  lng: number,
  route: RoutePolyline,
): SnapResult {
  const points = route.points
  if (points.length === 0) {
    return {
      snappedLat: lat,
      snappedLon: lng,
      distanceMeters: Infinity,
      alongMeters: 0,
      lateralOffsetMeters: Infinity,
      routeId: route.routeId,
    }
  }

  if (points.length === 1) {
    const d = fastDistance({ latitude: lat, longitude: lng }, points[0])
    return {
      snappedLat: points[0].latitude,
      snappedLon: points[0].longitude,
      distanceMeters: d,
      alongMeters: 0,
      lateralOffsetMeters: d,
      routeId: route.routeId,
    }
  }

  // Convert point to local meters (equirectangular projection)
  const cosLat = Math.cos(lat * Math.PI / 180)
  const px = lng * METERS_PER_DEG_LAT * cosLat
  const py = lat * METERS_PER_DEG_LAT

  let bestDist = Infinity
  let bestSnappedLat = lat
  let bestSnappedLng = lng
  let bestAlongMeters = 0

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]

    // Convert segment endpoints to local meters
    const ax = a.longitude * METERS_PER_DEG_LAT * cosLat
    const ay = a.latitude * METERS_PER_DEG_LAT
    const bx = b.longitude * METERS_PER_DEG_LAT * cosLat
    const by = b.latitude * METERS_PER_DEG_LAT

    // Project point onto segment
    const dx = bx - ax
    const dy = by - ay
    const segLenSq = dx * dx + dy * dy

    let t: number
    if (segLenSq < 0.0001) {
      // Degenerate segment (a ≈ b)
      t = 0
    } else {
      // Parameter t: 0 = at A, 1 = at B
      t = ((px - ax) * dx + (py - ay) * dy) / segLenSq
      t = Math.max(0, Math.min(1, t)) // Clamp to segment
    }

    // Closest point on segment
    const cx = ax + t * dx
    const cy = ay + t * dy

    // Distance from input point to closest point
    const distX = px - cx
    const distY = py - cy
    const dist = Math.sqrt(distX * distX + distY * distY)

    if (dist < bestDist) {
      bestDist = dist

      // Convert back to lat/lng
      bestSnappedLat = cy / METERS_PER_DEG_LAT
      bestSnappedLng = cx / (METERS_PER_DEG_LAT * cosLat)

      // Calculate along-route distance: cumulative to segment start + fraction of segment
      const segLen = Math.sqrt(segLenSq)
      bestAlongMeters = route.cumulativeMeters[i] + t * segLen
    }
  }

  return {
    snappedLat: bestSnappedLat,
    snappedLon: bestSnappedLng,
    distanceMeters: bestDist,
    alongMeters: bestAlongMeters,
    lateralOffsetMeters: bestDist,
    routeId: route.routeId,
  }
}

/**
 * Snap to the best route from multiple route polylines.
 * Returns the snap result with smallest distance.
 */
export function snapToNearestRoute(
  lat: number,
  lng: number,
  routes: RoutePolyline[],
): SnapResult | null {
  if (routes.length === 0) return null

  let best: SnapResult | null = null

  for (const route of routes) {
    const result = snapToRoute(lat, lng, route)
    if (!best || result.distanceMeters < best.distanceMeters) {
      best = result
    }
  }

  return best
}

// ── Fast Distance ───────────────────────────────────────────────

/** Fast approximate distance in meters (equirectangular, ~0.1% error at 46°N) */
function fastDistance(a: GpsPoint, b: GpsPoint): number {
  const cosLat = Math.cos(((a.latitude + b.latitude) / 2) * Math.PI / 180)
  const dx = (b.longitude - a.longitude) * METERS_PER_DEG_LAT * cosLat
  const dy = (b.latitude - a.latitude) * METERS_PER_DEG_LAT
  return Math.sqrt(dx * dx + dy * dy)
}
